import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { AlertModal } from '@/components/modal/alert-modal';
import { showToast } from '@/components/ui/show-toast';
import { Trash2, Edit } from 'lucide-react';
import { useDeleteOrganisationMutation } from '@/services/organisations/queries';
import { useAuthStore } from '@/store/auth';

export default function OrganisationSettings({ organisationId }: { organisationId: string }) {
  const navigate = useNavigate();
  const logout = useAuthStore((state) => state.clearAuthData);
  const userRole = useAuthStore((state) => state.user?.organisation?.role);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleUpdateProfile = () => {
    navigate('/dashboard/settings/edit-organisation');
  };

  const {mutate: deleteOrganisation, isPending} = useDeleteOrganisationMutation(
    () => {
      showToast('success', 'Organisation deleted successfully');
      // logout and navigate to login page
      logout();
      setTimeout(() => navigate('/sign-in', { replace: true }), 0);
    },
    (error) => {
      showToast('error', error?.response?.data?.message || 'Failed to delete organisation');
    }
  );

  return (
    <div className='space-y-5 px-6'>
      {/* Profile Update Section */}
      <Card className='max-w-2xl bg-white border border-gray-200'>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-3'>
            <div className='rounded-lg bg-blue-100 p-2'>
              <Edit className='h-4.5 w-4.5 text-blue-600' />
            </div>
            <div>
              <CardTitle className='text-base'>Update Profile</CardTitle>
              <CardDescription className='text-sm'>
                Update your organisation details and information
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className='pt-0'>
          <div className='space-y-4'>
            <div>
              <h4 className='font-medium text-gray-900 text-sm'>Organisation Profile</h4>
              <p className='text-sm text-gray-600 mt-1'>
                Update your organisation profile with detailed information, contact details, and media.
              </p>
            </div>
            <Button 
              onClick={handleUpdateProfile}
              size='sm'
              className='w-fit'
            >
              Update Profile
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Management Section */}
      { userRole === 'org_admin' && (
      <Card className='max-w-2xl'>
        <CardHeader className='pb-4'>
          <div className='flex items-center gap-3'>
            <div className='rounded-lg bg-red-100 p-2'>
              <Trash2 className='h-4.5 w-4.5 text-red-600' />
            </div>
            <div>
              <CardTitle className='text-base text-red-600'>Danger Zone</CardTitle>
              <CardDescription className='text-sm'>
                Irreversible and destructive actions
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className='pt-0'>
          <div className='space-y-4'>
            <div>
              <h4 className='font-medium text-gray-900 text-sm'>Delete Organisation</h4>
              <p className='text-sm text-gray-600 mt-1'>
                Once you delete your organisation, there is no going back. Please be certain.
              </p>
            </div>
            <Button 
              variant='destructive'
              size='sm'
              onClick={() => setShowDeleteModal(true)}
              className='w-fit'
            >
              Delete Organisation
            </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      <AlertModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => deleteOrganisation(organisationId)}
        loading={isPending}
        title='Delete Organisation'
        description='Are you sure you want to delete this organisation? This action cannot be undone and will permanently remove all organisation data.'
      />
    </div>
  );
}